import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTooltip } from '@/context/TooltipContext';

const Tooltip = ({
  content,
  children,
  position = 'top',
  tooltipId,
  iconId,
  onClick,
  onOpen,
  onClose,
  open,
  arrow,
  title,
  placement,
  style,
  onClickFix = false,
  className,
}) => {
  const {
    openTooltip,
    hoveredIcon,
    openTooltipHandler,
    closeTooltipHandler,
    setHoveredIconHandler,
    clearHoveredIconHandler,
  } = useTooltip();

  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [actualPosition, setActualPosition] = useState(position);
  const [isPositioned, setIsPositioned] = useState(false);
  const [isClickFixed, setIsClickFixed] = useState(false);
  const [tooltipSize, setTooltipSize] = useState('normal');
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const shouldBeOpen =
    open !== undefined ? open : openTooltip === tooltipId || isClickFixed;
  const isHovered = iconId ? hoveredIcon === iconId : false;

  useEffect(() => {
    setIsVisible(shouldBeOpen);
  }, [shouldBeOpen]);

  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const tooltipElement = tooltipRef.current;
      const textContent = title || content;

      if (textContent) {
        const tempElement = document.createElement('div');
        tempElement.style.cssText = `
          position: absolute;
          visibility: hidden;
          white-space: nowrap;
          font-size: 14px;
          font-family: inherit;
          padding: 8px 12px;
        `;
        tempElement.textContent = textContent;
        document.body.appendChild(tempElement);

        const textWidth = tempElement.offsetWidth;

        document.body.removeChild(tempElement);

        if (textWidth < 100) {
          setTooltipSize('small');
        } else if (textWidth < 200) {
          setTooltipSize('normal');
        } else {
          setTooltipSize('large');
        }
      }
    }
  }, [isVisible, title, content]);

  useEffect(() => {
    if (isVisible) {
      const handleCalculatePosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight,
        };

        let newPosition = position;
        let top = 0;
        let left = 0;

        switch (position) {
          case 'top':
            top = triggerRect.top - tooltipRect.height - 8;
            left =
              triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'bottom':
            top = triggerRect.bottom + 8;
            left =
              triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'left':
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            left = triggerRect.left - tooltipRect.width - 8;
            break;
          case 'right':
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            left = triggerRect.right + 8;
            break;
          default:
            top = triggerRect.top - tooltipRect.height - 8;
            left =
              triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
            break;
        }

        // Check if tooltip goes off screen and adjust position
        if (left < 8) {
          left = 8;
          if (position === 'top' || position === 'bottom') {
          } else {
            newPosition = 'right';
            left = triggerRect.right + 8;
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          }
        }

        if (left + tooltipRect.width > viewport.width - 8) {
          left = viewport.width - tooltipRect.width - 8;
          if (position === 'top' || position === 'bottom') {
          } else {
            newPosition = 'left';
            left = triggerRect.left - tooltipRect.width - 8;
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
          }
        }

        if (top < 8) {
          if (position === 'top') {
            newPosition = 'bottom';
            top = triggerRect.bottom + 8;
          } else {
            top = 8;
          }
        }

        if (top + tooltipRect.height > viewport.height - 8) {
          if (position === 'bottom') {
            newPosition = 'top';
            top = triggerRect.top - tooltipRect.height - 8;
          } else {
            top = viewport.height - tooltipRect.height - 8;
          }
        }

        setTooltipPosition({ top, left });
        setActualPosition(newPosition);
        setIsPositioned(true);
      };

      // Use requestAnimationFrame to ensure the tooltip is rendered before positioning
      requestAnimationFrame(() => {
        handleCalculatePosition();
      });

      const handleResize = () => handleCalculatePosition();
      const handleScroll = () => handleCalculatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      // Reset positioning state when tooltip becomes invisible
      setIsPositioned(false);
    }
  }, [isVisible, position]);

  const getArrowStyle = () => {
    if (!triggerRef.current) return {};

    const triggerRect = triggerRef.current.getBoundingClientRect();

    const arrowStyles = {
      top: {
        position: 'absolute',
        top: '100%',
        left: triggerRect.left + triggerRect.width / 2 - tooltipPosition.left,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderTop: '8px solid #000000',
      },
      bottom: {
        position: 'absolute',
        bottom: '100%',
        left: triggerRect.left + triggerRect.width / 2 - tooltipPosition.left,
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #000000',
      },
      left: {
        position: 'absolute',
        left: '100%',
        top: triggerRect.top + triggerRect.height / 2 - tooltipPosition.top - 8,
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderLeft: '8px solid #000000',
      },
      right: {
        position: 'absolute',
        right: '100%',
        top: triggerRect.top + triggerRect.height / 2 - tooltipPosition.top - 8,
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderRight: '8px solid #000000',
      },
    };

    return arrowStyles[actualPosition] || {};
  };

  const getTooltipClasses = () => {
    const baseClasses =
      'bg-black text-white text-sm rounded-md px-3 py-2 shadow-lg';

    switch (tooltipSize) {
      case 'small':
        return `${baseClasses} text-xs px-2 py-1 whitespace-nowrap`;
      case 'normal':
        return `${baseClasses} whitespace-nowrap`;
      case 'large':
        return `${baseClasses} whitespace-normal max-w-xs`;
      default:
        return `${baseClasses} whitespace-nowrap`;
    }
  };

  const TooltipPortal = () => {
    if (!isVisible) return null;

    return ReactDOM.createPortal(
      <div
        ref={tooltipRef}
        style={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          zIndex: 999999999,
          opacity: isPositioned ? 1 : 0,
          transition: 'opacity 0.1s ease-in-out',
        }}
        className="pointer-events-none">
        <div className={getTooltipClasses()}>{title || content}</div>
        <div style={getArrowStyle()}></div>
      </div>,
      document.body
    );
  };

  const handleMouseEnter = (e) => {
    e.stopPropagation();
    if (iconId) {
      setHoveredIconHandler(iconId);
    }
    if (tooltipId) {
      openTooltipHandler(tooltipId);
    }
    if (onOpen) {
      onOpen(e);
    }
  };

  const handleMouseLeave = (e) => {
    e.stopPropagation();

    if (onClickFix && isClickFixed) {
      return;
    }

    if (tooltipId) {
      closeTooltipHandler();
    }
    if (iconId) {
      clearHoveredIconHandler();
    }
    if (onClose) {
      onClose(e);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();

    if (onClickFix) {
      setIsClickFixed(!isClickFixed);
    } else {
      if (tooltipId) {
        closeTooltipHandler();
      }
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        style={style}
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="cursor-default">
        {children}
      </div>
      <TooltipPortal />
    </div>
  );
};

export default Tooltip;
